import React, { memo, useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { isEmptyString } from '@common/utils';

import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatSignPad from '@components/common/satUI/SatSignPad';
import SatButton from '@components/common/satUI/SatButton';

/**
 * 전자서명 동의 팝업
 */
const ForeignCurrencySuitabilityDigitalSignaturePopup = ({
  currentStep,
  signData,
  customerName,
  handleSignButtonClick,
}) => {
  const canvasNameRef = useRef();
  const canvasSignRef = useRef();

  const [signName, setSignName] = useState(''); // 성명 (base64)
  const [signature, setSignature] = useState(''); // 서명 (base64)

  const [showError, setShowError] = useState(false); // 에러메세지 표기여부
  const [pasteSignData, setPasteSignData] = useState(false); // 기존 사인데이터 불러오기 여부

  useEffect(() => {
    const cleanUp = () => { };
    return cleanUp;
  }, []);

  const getErrors = useCallback((showError, signName, signature) => {
    return {
      signName: showError && isEmptyString(signName),
      signature: showError && isEmptyString(signature),
    };
  }, []);

  /**
   * 전자 서명, 성명 저장
   */
  const handleConfirm = useCallback((signName, signature, e) => {
    const errors = getErrors(true, signName, signature);

    if (Object.values(errors).every(a => false === a)) {
      handleSignButtonClick(signName, signature, e);
    } else {
      setShowError(() => true);
    }
  }, []);

  return (
    <>
      {currentStep.main === 1 && currentStep.sub === 2 && (
        <>
          <div className="cont">
            <h3 className="layer-tit2">전자문서 서명 동의</h3>
            <div className="consent-scroll" id="signPadScroll">
              <div className="info-box bg-yellow">
                <p className="text-info3 dot-type">
                  등록하신 고객의 성명과 서명은 이후 모든 청약서류에 붙여넣기가
                  가능합니다.
                </p>
                <p className="text-info3 dot-type">
                  주요 내용 확인 후, 고객 성명 및 서명란에 &lsquo;성명/서명
                  붙여넣기&rsquo;를 선택하면 됩니다.
                </p>
              </div>

              <div className="sign-pad-wrap">
                <SatSignPad
                  canvasRef={canvasNameRef}
                  name={customerName}
                  title="고객 성명 입력"
                  error={
                    getErrors(showError, signName, signature).signName
                      ? '성명을 입력해주세요.'
                      : ''
                  }
                  onChange={data => setSignName(data)}
                  signData={pasteSignData ? signData?.name ?? '' : ''}
                />
                <SatSignPad
                  canvasRef={canvasSignRef}
                  name="서명"
                  title="고객 서명 입력"
                  error={
                    getErrors(showError, signName, signature).signature
                      ? '서명을 입력해주세요.'
                      : ''
                  }
                  onChange={data => setSignature(data)}
                  signData={pasteSignData ? signData?.sign ?? '' : ''}
                />
              </div>
            </div>
          </div>

          <div className="layer-footer">
            <SatButton
              styleClass={`btn-s blue btn-wide ${Object.values(getErrors(showError, signName, signature)).some(
                a => true === a,
              )
                ? 'disabled'
                : ''
                }`}
              label="확인"
              onClick={e => handleConfirm(signName, signature, e)}
            />
          </div>
        </>
      )}

      {[2, 3].includes(currentStep.main) && (
        <>
          <header className="modal-header">
            <h3 className="layer-tit2">외화보험 적합성 진단</h3>
          </header>

          <div className="cont">
            <div className="modal-scroll-wrap" id="signPadScroll">
              <div className="info-box bg-yellow">
                {currentStep.main === 2 && (
                  <>
                    <p className="text-info3 dot-type">
                      본인은 상기와 같이 제공한 정보의 내용이 정확한 것임을
                      확인하고, 이를 바탕으로 적합성 진단을 실시하고자합니다.
                    </p>
                    <p className="text-info3 dot-type">
                      본인은 귀사가 본인으로부터 제공받은 정보를 향후 24개월
                      동안 정보가 변경되지 않은 것으로 간주 한다는 점을
                      설명받았습니다.
                    </p>
                    <p className="text-info3 dot-type">
                      본인은 보험계약자 정보에 변경사항이 발생한 경우 이를
                      귀사에 통지하여야 귀사가 본인에게 적합한 보험계약 권유를
                      할 수 있다는 점을 설명받았습니다.
                    </p>
                  </>
                )}
                {currentStep.main === 3 && (
                  <p className="text-info3 dot-type">
                    본인이 제공한 정보를 바탕으로 본인의 보험계약자 성향 분석
                    결과에 대해 안내 받았음을 확인합니다.
                  </p>
                )}
              </div>
              <div className="check-wrap small w-360">
                <SatCheckBox
                  value={[pasteSignData]}
                  type={'radio'}
                  options={[{ label: '저장했던 성명/서명을 붙여넣으세요.' }]}
                  onChange={() => setPasteSignData(prev => !prev)}
                  customStyleClass="small w-360"
                />
              </div>

              <div className="sign-pad-wrap">
                <SatSignPad
                  canvasRef={canvasNameRef}
                  name={customerName}
                  title="고객 성명 입력"
                  error={
                    getErrors(showError, signName, signature).signName
                      ? '성명을 입력해주세요.'
                      : ''
                  }
                  onChange={data => setSignName(data)}
                  signData={pasteSignData ? signData?.signName ?? '' : ''}
                  pasteCheck={pasteSignData}
                  onChangePasteCheck={setPasteSignData}
                />
                <SatSignPad
                  canvasRef={canvasSignRef}
                  name="서명"
                  title="고객 서명 입력"
                  error={
                    getErrors(showError, signName, signature).signature
                      ? '서명을 입력해주세요.'
                      : ''
                  }
                  onChange={data => setSignature(data)}
                  signData={pasteSignData ? signData?.signature ?? '' : ''}
                  pasteCheck={pasteSignData}
                  onChangePasteCheck={setPasteSignData}
                />
              </div>
            </div>
          </div>

          <div className="layer-footer">
            <SatButton
              styleClass={`btn-s blue btn-wide ${Object.values(getErrors(showError, signName, signature)).some(
                a => true === a,
              )
                ? 'disabled'
                : ''
                }`}
              label="확인"
              onClick={e => handleConfirm(signName, signature, e)}
            />
          </div>
        </>
      )}
    </>
  );
};

ForeignCurrencySuitabilityDigitalSignaturePopup.propTypes = {
  currentStep: PropTypes.shape({
    main: PropTypes.number,
    sub: PropTypes.number,
  }).isRequired,

  signData: PropTypes.shape({
    name: PropTypes.any,
    sign: PropTypes.any,
  }),
  customerName: PropTypes.string.isRequired,
  handleSignButtonClick: PropTypes.func.isRequired,
};

export default memo(ForeignCurrencySuitabilityDigitalSignaturePopup);
